import React, { FC, useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { createPortal } from "react-dom";
import { IoMdClose } from "react-icons/io";
import Overlay from "../../../checkEligibilty/components/modals/Overlay";
import { ContainerVariants } from "../../../checkEligibilty/components/modals/variant";
import { Form, Formik } from "formik";
import { object, string } from "yup";
import InputField from "../../../components/formik fields/InputField";
import useNavigateToFibiVoice from "../../../../../hooks/useNavigateToFibiVoice";
import { Link } from "react-router-dom";
import { Sparkles, Pause, Play } from "lucide-react";
import { PiUser } from "react-icons/pi";
import { AudioVisualizer } from "../voice/components/AudioVisualizer";
import TypingIndicator from "../../../landing page/components/TypingIndicator";
import { toast } from "react-toastify";
import Markdown from "markdown-to-jsx";
import useWebSocket from "react-use-websocket";
import { BaseFiBiInterviewWSEndpointB2B } from "../../../../../api/request";
import {
  getBinaryChunk,
  getErrorMessage,
  getIsMediaSourceSupported,
  isBinaryChunkResponse,
  isEnd,
  isError,
  isTextResponse,
} from "../voice/components/data";
import useFibiMediaSource from "../voice/components/useFibiMediaSource";
import useFibiAudioContext from "../voice/components/useFibiAudioContext";

interface Props {
  onClose: () => void;
}

const InterviewSampleModal: FC<Props> = ({ onClose }) => {
  const { url } = useNavigateToFibiVoice();
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<
    { text: string; userType: "system" | "user" }[]
  >([]);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const formatText = (text: string) => {
    return <Markdown>{text}</Markdown>;
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages.length]);

  const isMediaSourceSupported = getIsMediaSourceSupported();

  const [fibiProcessing, setFibiProcessing] = useState(false);
  const [fibiSpeaking, setFibiSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const handlePlaybackEnded = useCallback(() => {
    setFibiProcessing(false);
    setFibiSpeaking(false);
  }, []);

  const handlePlaybackPlaying = useCallback(() => {
    setFibiSpeaking(true);
  }, []);

  const {
    processQueuedChunks,
    pendingChunks,
    audioElement,
    endOfStreamReceived,
    initNewStream,
    sourceBufferRef,
  } = useFibiMediaSource({
    handlePlaybackEnded,
    handlePlaybackPlaying,
    enabled: isMediaSourceSupported,
  });

  const {
    setNetworkStatus,
    setIsNetworkError,
    lastStatusUpdateRef,
    lastChunkReceivedAtRef,
    ensureAudioContext,
    processChunk,
    chunkArrivalTimesRef,
    networkErrorTimeoutRef,
    totalChunksReceivedRef,
    audioContextRef,
    resetAudioState,
    pauseAudio,
    resumeAudio,
  } = useFibiAudioContext({
    setFibiSpeaking,
    setFibiProcessing,
    startListening: () => {},
    enabled: !isMediaSourceSupported,
    isPaused,
    setIsPaused,
  });

  const theNotEnd =
    messages.filter((msg) => msg.userType === "system").length < 2;

  const { sendJsonMessage } = useWebSocket(BaseFiBiInterviewWSEndpointB2B, {
    share: true,
    shouldReconnect: () => true,
    onOpen: () => {
      if (messages.filter((msg) => msg.userType === "system").length >= 1)
        return;
      // Send initial message with only isAnonUser: true
      const initialMessage = {
        isAnonUser: true,
        isPcm: !isMediaSourceSupported,
      };
      setFibiProcessing(true);
      sendJsonMessage(initialMessage);
    },
    onMessage: async (event) => {
      const data = JSON.parse(event.data);
      const binaryChunk = getBinaryChunk(data);
      const isBinaryChunk = isBinaryChunkResponse(data);
      if (fibiProcessing) {
        setFibiProcessing(false);
      }

      const now = Date.now();

      if (isTextResponse(data)) {
        const text = (data as any).content;
        const id = (data as any).id;

        // Store the id from the first response
        if (id && !sessionId) {
          setSessionId(id);
        }

        if (text)
          setMessages((prev) => [...prev, { text, userType: "system" }]);
      } else if (isBinaryChunk && binaryChunk) {
        if (isMediaSourceSupported) {
          if (!audioElement.current) return;

          const arrayBuffer = binaryChunk.data;
          const uint8Array = new Uint8Array(arrayBuffer);

          pendingChunks.current.push(uint8Array);

          processQueuedChunks();

          if (
            !audioElement.current.paused ||
            pendingChunks.current.length === 1
          ) {
            audioElement.current.play().catch(console.error);
          }
        } else {
          const bin = getBinaryChunk(data);
          if (!bin) return;
          // DIAGNOSTIC: Track arrival times
          if (lastChunkReceivedAtRef.current !== null) {
            const diff = now - lastChunkReceivedAtRef.current;
            const timeSinceLastStatus = now - lastStatusUpdateRef.current;
            chunkArrivalTimesRef.current.push(diff);
            // Clear any existing timeout
            if (networkErrorTimeoutRef.current) {
              clearTimeout(networkErrorTimeoutRef.current);
            }
            // Only update status if it's been at least 1000ms since last update
            if (timeSinceLastStatus >= 1000) {
              if (diff > 200) {
                console.warn(
                  `⚠️ Network jitter detected: ${diff}ms between chunks`
                );
                setNetworkStatus("unstable");
                setIsNetworkError(true);
              } else {
                setNetworkStatus("stable");
                setIsNetworkError(false);
              }
              lastStatusUpdateRef.current = now;
            }
          }
          lastChunkReceivedAtRef.current = now;
          totalChunksReceivedRef.current += 1;
          if (!audioContextRef.current) {
            await ensureAudioContext();
            setFibiSpeaking(true);
          }
          await processChunk(new Uint8Array(bin.data));
        }
      } else if (isEnd(data)) {
        if (isMediaSourceSupported) {
          endOfStreamReceived.current = true;
          processQueuedChunks();
        } else {
          await processChunk(new Uint8Array(), true);
        }
      }

      if (isError(data)) {
        toast.error(getErrorMessage(data));
      }
    },
    onError: () => toast.error("WebSocket failed. Refresh to retry."),
    heartbeat: { message: "ping", timeout: 60000 },
  });

  const onSubmit = (message: string) => {
    const temp = message.trim();
    if (temp.length === 0) return toast.error("Please enter your response");

    // Don't allow submitting if we don't have a sessionId yet
    if (!sessionId) {
      return toast.error("Please wait for the system to initialize");
    }

    if (isMediaSourceSupported) {
      initNewStream();
      if (sourceBufferRef.current && !sourceBufferRef.current.updating) {
        const buffered = sourceBufferRef.current.buffered;
        if (buffered.length > 0) {
          const removeStart = buffered.start(0); // often this is 0
          const removeEnd = buffered.end(buffered.length - 1);
          sourceBufferRef.current.remove(removeStart, removeEnd);
        }
      }
    } else {
      resetAudioState();
    }

    // Reset pause state for new message
    setIsPaused(false);

    setFibiProcessing(true);
    setMessages((prev) => [...prev, { text: temp, userType: "user" }]);

    const dataToSend = {
      isAnonUser: true,
      id: sessionId,
      userMessage: temp,
      isPcm: !isMediaSourceSupported,
    };

    try {
      sendJsonMessage(dataToSend);
    } catch (error) {
      console.error(error);
    } finally {
      return;
    }
  };

  const togglePause = () => {
    if (isMediaSourceSupported && audioElement.current) {
      // Handle MediaSource pause/resume
      if (isPaused) {
        audioElement.current.play().catch(console.error);
        setIsPaused(false);
      } else {
        audioElement.current.pause();
        setIsPaused(true);
      }
    } else {
      // Handle AudioContext pause/resume
      if (isPaused) {
        resumeAudio();
      } else {
        pauseAudio();
      }
    }
  };

  const isReading = fibiSpeaking;
  const isLoading = fibiProcessing;

  return createPortal(
    <Overlay onClose={onClose}>
      <motion.div
        onClick={(e) => e.stopPropagation()}
        variants={ContainerVariants}
        className=" w-[90vw] sm:w-[700px] h-max bg-gray-100 rounded-xl sm:px-0"
      >
        <div className="h-full py-10 px-4 relative pb-0">
          <div className="w-full mx-auto">
            <div className="mb-8">
              <IoMdClose
                onClick={onClose}
                className="cursor-pointer absolute right-3 top-2 bg-white"
                size={24}
              />
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 text-center">
                Think You’re Interview-Ready? <br />
                Try This Visa Question!
              </h2>
            </div>

            <Formik
              initialValues={{
                response: "",
              }}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values.response);
                resetForm();
              }}
              validationSchema={object({
                response: string().required("Required"),
              })}
            >
              {(formik) => (
                <Form>
                  <div
                    ref={chatContainerRef}
                    className="h-[70vh] sm:h-[500px] w-full relative overflow-y-auto sm:px-6"
                  >
                    <div className="flex flex-col h-full">
                      <div className="flex-1 flex flex-col gap-4 py-3">
                        {messages.map((msg, index) => {
                          if (msg.userType === "system") {
                            // Find the last index of system messages
                            const systemMessageIndices = messages
                              .map((m, i) => (m.userType === "system" ? i : -1))
                              .filter((i) => i !== -1);
                            const lastSystemIndex =
                              systemMessageIndices[
                                systemMessageIndices.length - 1
                              ];
                            const isLatestSystem = index === lastSystemIndex;

                            return (
                              <div key={index} className="flex gap-2">
                                <div className="w-10 h-10 rounded-full bg-primaryColor flex items-center justify-center flex-shrink-0">
                                  <Sparkles className="text-white" size={20} />
                                </div>
                                <div className="p-6 rounded-xl bg-white shadow-sm">
                                  <div className="flex flex-col whitespace-pre-line">
                                    {formatText(msg.text)}
                                    <div className="flex items-center gap-2 ml-auto">
                                      <AudioVisualizer
                                        audioLevel={0}
                                        isActive={
                                          isReading &&
                                          isLatestSystem &&
                                          !isPaused
                                        } // Only show active for latest system message and not paused
                                        type="output"
                                        className="w-16 h-6"
                                      />

                                      {/* Pause/Resume button - only show for latest system message when reading */}
                                      {isLatestSystem &&
                                        (isReading || isPaused) && (
                                          <button
                                            type="button"
                                            onClick={togglePause}
                                            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                                            title={
                                              isPaused
                                                ? "Resume reading"
                                                : "Pause reading"
                                            }
                                          >
                                            {isPaused ? (
                                              <Play
                                                size={16}
                                                className="text-primaryColor"
                                              />
                                            ) : (
                                              <Pause
                                                size={16}
                                                className="text-primaryColor"
                                              />
                                            )}
                                          </button>
                                        )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          } else if (msg.userType === "user") {
                            return (
                              <div key={index} className="flex gap-2 ml-auto">
                                <div className="p-6 rounded-xl bg-white shadow-sm">
                                  {formatText(msg.text)}
                                </div>

                                <div className="w-10 h-10 rounded-full bg-primaryColor flex items-center justify-center flex-shrink-0">
                                  <PiUser className="text-white" size={20} />
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })}

                        {isLoading && (
                          <div className="mb-10">
                            <TypingIndicator />
                          </div>
                        )}
                      </div>

                      {/* footer */}
                      <div className="flex flex-col gap-2 sticky bottom-0 left-0 w-full bg-gray-100 pb-4">
                        {/* Hide input block after the second system message comes in */}
                        {theNotEnd && (
                          <div className="flex flex-col [&_textarea]:bg-transparent [&_textarea]:!border-none [&_textarea]:!p-0 [&_textarea]:!h-[40px] border border-primaryColor/70 bg-white rounded-xl p-4 shadow-sm">
                            <InputField
                              name="response"
                              label=""
                              placeholder="Type your response here..."
                              inputType="text-area"
                              borderRadius="0px"
                            />
                            <button
                              disabled={isLoading || !formik.isValid}
                              className={`ml-auto bg-primaryColor text-white py-2 px-6 rounded-md ${
                                isLoading || !formik.isValid
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                            >
                              Send
                            </button>
                          </div>
                        )}

                        <div
                          className={`w-full flex items-center justify-center text-center text-sm ${
                            !theNotEnd ? "pt-6" : ""
                          }`}
                        >
                          Master every question—{" "}
                          <Link
                            to={url}
                            onClick={onClose}
                            className=" text-primaryColor underline"
                          >
                            Sign up to unlock free full access
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </motion.div>
    </Overlay>,
    document.body
  );
};

export default InterviewSampleModal;
